<script setup>
  import {ref} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";

  defineOptions({
    name: "ComponentSpotlight",
  });

  const props = defineProps({
    suppressed: {
      type: Boolean,
      default: false,
    },
    storageKey: {
      type: String,
      required: true,
    },
    storageIdentifier: {
      type: String,
      required: false,
      default: null,
    },
    icon: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
  });

  function getDismissedIdentifiers() {
    try {
      const stored = JSON.parse(localStorage.getItem(props.storageKey));

      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }

  function isDismissedInStorage() {
    if (props.storageIdentifier === null) {
      return localStorage.getItem(props.storageKey) === "true";
    }

    return getDismissedIdentifiers().includes(props.storageIdentifier);
  }

  const dismissed = ref(props.suppressed || isDismissedInStorage());

  function dismiss() {
    dismissed.value = true;

    if (props.storageIdentifier === null) {
      localStorage.setItem(props.storageKey, "true");

      return;
    }

    const identifiers = new Set(getDismissedIdentifiers());
    identifiers.add(props.storageIdentifier);
    localStorage.setItem(props.storageKey, JSON.stringify([...identifiers]));
  }
</script>

<template>
  <div class="relative inline-flex" :data-test="getTestId('spotlight')">
    <template v-if="!dismissed">
      <span
        aria-hidden="true"
        class="absolute inset-0 -z-10 animate-ping rounded-full bg-mcblue-500/40 motion-reduce:animate-none"
      />

      <div
        class="absolute -top-9 left-1/2 z-1 flex -translate-x-1/2 items-center gap-1 rounded-full bg-linear-to-br from-mcblue-900 via-mcblue-700 to-mcblue-500 py-1 pr-1.5 pl-2.5 text-xs font-semibold whitespace-nowrap text-white shadow-lg"
      >
        <span v-if="icon || $slots.icon">
          <slot name="icon">
            <ComponentIcon class="text-sm">{{ icon }}</ComponentIcon>
          </slot>
        </span>

        <span v-if="label.length > 0">{{ label }}</span>

        <button
          type="button"
          class="grid size-4 shrink-0 place-items-center rounded-full text-white/60 hover:bg-white/10 hover:text-white"
          aria-label="Hinweis ausblenden"
          :data-test="getTestId('spotlight-close-button')"
          @click="dismiss"
        >
          <ComponentIcon class="text-xs leading-none">close</ComponentIcon>
        </button>

        <span aria-hidden="true" class="absolute -bottom-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-mcblue-700" />
      </div>
    </template>

    <slot />
  </div>
</template>
